import type { FC } from 'react';
import React, { useState } from 'react';
import { Link, SelectInput } from '@wix/cli-ui-kit';
import { Trans, useTranslation } from '@wix/cli-i18n';
import { useWixCliAppBi } from '../../../bi/index.js';
import { useGenerateCustomElement } from './custom-element/index.js';
import { useAddBlocksSupportToProject } from './blocks/index.js';
import { useWizardStep } from '../Wizard.js';

export type SiteWidgetType = 'customElement' | 'blocks';

const SelectSiteWidgetTypeStep: FC<{
  onSubmit: (siteWidgetType: SiteWidgetType) => void;
}> = ({ onSubmit }) => {
  const bi = useWixCliAppBi();
  const { t } = useTranslation();
  const { isActive, goToNextStep } = useWizardStep();

  return (
    <SelectInput<SiteWidgetType>
      isActive={isActive}
      label={t('generate_command.site_widget_wizard.choose_extension.title')}
      info={t('generate_command.site_widget_wizard.choose_extension.info')}
      options={[
        {
          title: t(
            'generate_command.site_widget_wizard.custom_element_option.title'
          ),
          description: (
            <Trans
              i18nKey="generate_command.site_widget_wizard.custom_element_option.description"
              components={[
                <Link url="https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/wix-cli/supported-extensions/site-extensions/site-widgets/add-a-site-widget-extension-in-the-cli" />,
              ]}
            />
          ),
          value: 'customElement',
        },
        {
          title: t('generate_command.site_widget_wizard.blocks.title'),
          description: (
            <Trans
              i18nKey="generate_command.site_widget_wizard.blocks.description"
              components={[
                <Link url="https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/wix-cli/supported-extensions/site-extensions/blocks-site-widgets/add-a-blocks-site-widget-extension-in-the-cli" />,
              ]}
            />
          ),
          value: 'blocks',
        },
      ]}
      onSubmit={(option) => {
        bi.cliFlowStepAnswered({
          question: t(
            'generate_command.site_widget_wizard.choose_extension.title'
          ),
          questionKey:
            'generate_command.site_widget_wizard.choose_extension.title',
          answer: option.title,
        });
        onSubmit(option.value);
        goToNextStep();
      }}
    />
  );
};

export const useGenerateSiteWidget = () => {
  const [siteWidgetType, setSiteWidgetType] = useState<SiteWidgetType>();
  const generateCustomElementSteps = useGenerateCustomElement();
  const addBlocksSupportToProjectSteps = useAddBlocksSupportToProject({
    siteWidgetType,
  });

  return [
    <SelectSiteWidgetTypeStep onSubmit={setSiteWidgetType} />,
    ...(siteWidgetType === 'customElement' ? generateCustomElementSteps : []),
    ...(siteWidgetType === 'blocks' ? addBlocksSupportToProjectSteps : []),
    ...(siteWidgetType === undefined ? [<></>] : []),
  ];
};
